// src/components/LinkCardsView.tsx
import { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import LinkSquare from '../components/common/cards/LinkSquare';
import { ILink } from '../interfaces/Link';
import Loading from '../components/common/Loading';

interface LinkCardsViewProps {
  isDelete: boolean;
  selectedData: string[];
  cardLinks: ILink[];
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  isError?: boolean;
  handleSelectLink: (value: string) => void;
  editModalOpen: (link: ILink) => void;
  detailsModelOpen: (id: string) => void;
  shareModalOpen: (id: string) => void;
  scrollToShortUrl: string | null;
  setScrollToShortUrl: (shortUrl: string | null) => void;
}

const LinkCardsView: React.FC<LinkCardsViewProps> = ({
  isDelete,
  selectedData,
  cardLinks,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  isError,
  handleSelectLink,
  editModalOpen,
  detailsModelOpen,
  shareModalOpen,
  scrollToShortUrl,
  setScrollToShortUrl,
}) => {
  const linkRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const { ref: sentinelRef, inView } = useInView({
    threshold: 0,
    triggerOnce: false,
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && !isError) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  useEffect(() => {
    if (scrollToShortUrl) {
      const targetLink = cardLinks.find(
        (link) =>
          link.shortUrl === scrollToShortUrl ||
          (link.subDomain
            ? `https://${link.subDomain}.${link.domain ?? 'linkfluencer.io'}/${link.shortUrl}`
            : `https://${link.domain ?? 'linkfluencer.io'}/${link.shortUrl}`) ===
            scrollToShortUrl,
      );
      if (targetLink) {
        const linkElement = linkRefs.current.get(targetLink._id);
        if (linkElement) {
          linkElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Apply highlight effect
          linkElement.classList.add('highlight');
          // Remove highlight effect after 2 seconds
          setTimeout(() => {
            linkElement.classList.remove('highlight');
            setScrollToShortUrl(null);
          }, 3000);
        }
      }
    }
  }, [scrollToShortUrl, cardLinks, setScrollToShortUrl]);

  return (
    <div className="my-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <style>
        {`
          .highlight {
            background-color: #e0f2fe; /* Light blue background for highlight */
            border: 2px solid #3b82f6; /* Blue border */
            transition: all 0.2s ease-in-out;
            border-radius: 16px;
          }
        `}
      </style>
      {cardLinks.length === 0 && (
        <div className="col-span-full flex justify-center items-center py-10 text-gray-500">
          {'No links found'}
        </div>
      )}
      {cardLinks.map((link: ILink) => (
        <div
          key={link._id}
          ref={(el) => {
            if (el) {
              linkRefs.current.set(link._id, el);
            } else {
              linkRefs.current.delete(link._id);
            }
          }}
        >
          <LinkSquare
            isDelete={isDelete}
            editModalOpen={() => editModalOpen(link)}
            detailsModelOpen={() => detailsModelOpen(link._id)}
            handleSelectLink={handleSelectLink}
            shareModalOpen={() => shareModalOpen(link._id)}
            selectedData={selectedData}
            link={link}
          />
        </div>
      ))}

      {isFetchingNextPage && (
        <Loading
          centeredToParent={true}
          style={{
            top: 'calc(50% + 60px)',
          }}
        />
      )}

      {cardLinks.length > 0 && hasNextPage && (
        <div ref={sentinelRef} className="col-span-full h-1" />
      )}
    </div>
  );
};

export default LinkCardsView;
